import type { EpisodeSummary } from "@/api";
import type { FolderSelection } from "../selection";

/**
 * 우측 미리보기의 선택 판정.
 *
 * 소유: 이 선택에 리뷰 표면(모드 스위처·라벨링 독)이 붙는지, 붙는다면 어느 에피소드 행인지,
 * 머리 줄에 무엇을 적는지. 판정이 한 곳에 있어야 좌측 목록의 하이라이트와 우측 pane 이 같은
 * 답을 쓴다.
 *
 * 소유하지 않음: 무엇을 그릴지의 분기(`PreviewPane` 이 `selection.kind` 위에서 한다),
 * 포맷 → 본문 표현(`filePreviewFormat`), 선택을 만드는 쪽(`FilesSidebar`·에피소드 목록).
 */

/**
 * 리뷰 표면이 붙는 선택이면 그 에피소드, 아니면 `null`.
 *
 * `channelVideo`(Files 모드의 단일 카메라)도 같은 에피소드에 귀속되므로 리뷰 표면이 붙는다.
 * 파일 선택은 리뷰 대상이 아니다. 리뷰는 에피소드 단위이고, 파일 하나에는 판정이 없다.
 *
 * `rows` 에 같은 uuid 가 있으면 그 행을 돌려준다. 그래서 목록·미리보기·독이 방금 저장한
 * override 를 같은 값으로 본다. 없으면(아직 받지 않은 페이지) 선택 당시 행을 그대로 쓴다.
 * `null` 을 돌려주면, 목록에서 사라진 에피소드를 보던 화면에서 독 전체가 사라진다.
 */
export function selectionReviewEpisode(
  selection: FolderSelection,
  rows: readonly EpisodeSummary[],
): EpisodeSummary | null {
  if (selection === null || (selection.kind !== "episode" && selection.kind !== "channelVideo")) {
    return null;
  }
  const { episode } = selection;
  return rows.find((row) => row.uuid === episode.uuid) ?? episode;
}

/** 미리보기 머리 줄. 형식 배지 · 이름 · 위치. */
export type PreviewHeadline = { badge: string; name: string; meta: string };

/**
 * 머리 줄에 표시할 내용. 에피소드 재생은 좌측 목록이 이미 이름을 보여 주므로 `null` 이다.
 * 그러면 소비처는 컨테이너도 만들지 않는다.
 */
export function selectionHeadline(selection: FolderSelection): PreviewHeadline | null {
  if (selection === null || selection.kind === "episode") {
    return null;
  }
  if (selection.kind === "channelVideo") {
    return {
      badge: "MP4",
      name: selection.channel,
      meta: episodeLabel(selection.episode),
    };
  }
  // 카탈로그와 무관하게 파일은 같은 자리에서 같게 보여야 한다.
  return {
    badge: selection.file.file_format.toUpperCase(),
    name: selection.file.filename,
    meta: selection.file.relative_path,
  };
}

function episodeLabel(episode: EpisodeSummary): string {
  return episode.display_name ?? `episode_${episode.episode_index}`;
}
